import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  LocalShipping,
  CallReceived,
  Send,
  History,
  Refresh,
  CheckCircle,
  Cancel,
  Schedule,
  Info,
  QrCodeScanner,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  locationsService,
  transfersService,
  Location,
  SampleTransfer,
  TransferStats,
} from '../services/locationsService';
import { isMainLab, isSatellite, getLocationConfig } from '../config/location.config';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const TransferDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [pendingOutgoing, setPendingOutgoing] = useState<SampleTransfer[]>([]);
  const [pendingIncoming, setPendingIncoming] = useState<SampleTransfer[]>([]);
  const [transferHistory, setTransferHistory] = useState<SampleTransfer[]>([]);
  const [stats, setStats] = useState<TransferStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Receive dialog
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<SampleTransfer | null>(null);
  const [receiveNotes, setReceiveNotes] = useState('');
  const [receiving, setReceiving] = useState(false);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const current = await locationsService.getCurrent();
      setCurrentLocation(current);

      if (current) {
        const [outgoing, incoming, history, transferStats] = await Promise.all([
          transfersService.getPendingFrom(current.id),
          transfersService.getPendingTo(current.id),
          transfersService.getHistory(current.id, 50),
          transfersService.getStats(current.id),
        ]);

        setPendingOutgoing(outgoing);
        setPendingIncoming(incoming);
        setTransferHistory(history);
        setStats(transferStats);
      }
    } catch (err) {
      console.error('Failed to load transfer data:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleReceiveTransfer = async () => {
    if (!selectedTransfer) return;

    try {
      setReceiving(true);
      await transfersService.receiveTransfer({
        transfer_id: selectedTransfer.id,
        notes: receiveNotes,
      });
      
      setReceiveDialogOpen(false);
      setSelectedTransfer(null);
      setReceiveNotes('');
      loadData(); // Refresh
    } catch (err) {
      console.error('Failed to receive transfer:', err);
      setError('Erreur lors de la réception du transfert');
    } finally {
      setReceiving(false);
    }
  };

  const handleCancelTransfer = async (transfer: SampleTransfer) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler ce transfert?')) return;

    try {
      await transfersService.cancelTransfer(transfer.id, 'Annulé par l\'utilisateur');
      loadData();
    } catch (err) {
      console.error('Failed to cancel transfer:', err);
      setError('Erreur lors de l\'annulation du transfert');
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
      PENDING: { label: 'En attente', color: 'warning' },
      IN_TRANSIT: { label: 'En transit', color: 'info' },
      RECEIVED: { label: 'Reçu', color: 'success' },
      PARTIAL: { label: 'Partiel', color: 'warning' },
      CANCELLED: { label: 'Annulé', color: 'error' },
    };
    
    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !currentLocation) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
          Transferts d'Échantillons
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadData}
            disabled={loading}
          >
            Actualiser
          </Button>
          {isSatellite() && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Send />}
              onClick={() => navigate('/preparation-mecanique')}
            >
              Nouveau Transfert
            </Button>
          )}
          {isMainLab() && (
            <Button
              variant="contained"
              color="success"
              startIcon={<QrCodeScanner />}
              onClick={() => navigate('/qr-scanner?mode=receive')}
            >
              Scanner pour Recevoir
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Transferts Envoyés
              </Typography>
              <Typography variant="h4">
                {stats?.totalSent || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Transferts Reçus
              </Typography>
              <Typography variant="h4">
                {stats?.totalReceived || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: pendingOutgoing.length > 0 ? 'warning.light' : undefined }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                En Transit (Sortant)
              </Typography>
              <Typography variant="h4">
                {stats?.pendingOut || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: pendingIncoming.length > 0 ? 'info.light' : undefined }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                En Attente (Entrant)
              </Typography>
              <Typography variant="h4">
                <Badge badgeContent={pendingIncoming.length} color="error">
                  {stats?.pendingIn || 0}
                </Badge>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={
              <Badge badgeContent={pendingIncoming.length} color="error">
                <CallReceived />
              </Badge>
            }
            label="À Recevoir"
            iconPosition="start"
          />
          <Tab
            icon={<Send />}
            label="En Transit"
            iconPosition="start"
          />
          <Tab
            icon={<History />}
            label="Historique"
            iconPosition="start"
          />
        </Tabs>

        {/* Incoming Transfers Tab */}
        <TabPanel value={tabValue} index={0}>
          <CardContent>
            {pendingIncoming.length === 0 ? (
              <Alert severity="info">
                Aucun transfert en attente de réception
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>N° Transfert</TableCell>
                      <TableCell>Lot</TableCell>
                      <TableCell>Origine</TableCell>
                      <TableCell align="center">Échantillons</TableCell>
                      <TableCell>Envoyé par</TableCell>
                      <TableCell>Date d'envoi</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingIncoming.map((transfer) => (
                      <TableRow key={transfer.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {transfer.transfer_number}
                          </Typography>
                        </TableCell>
                        <TableCell>{transfer.batch_number || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={transfer.from_location_code}
                            size="small"
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={transfer.sample_count}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>{transfer.sent_by_name || '-'}</TableCell>
                        <TableCell>{formatDate(transfer.sent_at)}</TableCell>
                        <TableCell>{getStatusChip(transfer.status)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Recevoir ce transfert">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CheckCircle />}
                              onClick={() => {
                                setSelectedTransfer(transfer);
                                setReceiveDialogOpen(true);
                              }}
                            >
                              Recevoir
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </TabPanel>

        {/* Outgoing Transfers Tab */}
        <TabPanel value={tabValue} index={1}>
          <CardContent>
            {pendingOutgoing.length === 0 ? (
              <Alert severity="info">
                Aucun transfert en transit
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>N° Transfert</TableCell>
                      <TableCell>Lot</TableCell>
                      <TableCell>Destination</TableCell>
                      <TableCell align="center">Échantillons</TableCell>
                      <TableCell>Date d'envoi</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingOutgoing.map((transfer) => (
                      <TableRow key={transfer.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {transfer.transfer_number}
                          </Typography>
                        </TableCell>
                        <TableCell>{transfer.batch_number || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={transfer.to_location_code}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={transfer.sample_count}
                            size="small"
                            color="default"
                          />
                        </TableCell>
                        <TableCell>{formatDate(transfer.sent_at)}</TableCell>
                        <TableCell>{getStatusChip(transfer.status)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Annuler le transfert">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleCancelTransfer(transfer)}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </TabPanel>

        {/* History Tab */}
        <TabPanel value={tabValue} index={2}>
          <CardContent>
            {transferHistory.length === 0 ? (
              <Alert severity="info">
                Aucun historique de transfert
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>N° Transfert</TableCell>
                      <TableCell>Lot</TableCell>
                      <TableCell>De</TableCell>
                      <TableCell>Vers</TableCell>
                      <TableCell align="center">Échantillons</TableCell>
                      <TableCell>Envoyé</TableCell>
                      <TableCell>Reçu</TableCell>
                      <TableCell>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transferHistory.map((transfer) => (
                      <TableRow key={transfer.id} hover>
                        <TableCell>{transfer.transfer_number}</TableCell>
                        <TableCell>{transfer.batch_number || '-'}</TableCell>
                        <TableCell>{transfer.from_location_code}</TableCell>
                        <TableCell>{transfer.to_location_code}</TableCell>
                        <TableCell align="center">{transfer.sample_count}</TableCell>
                        <TableCell>{formatDate(transfer.sent_at)}</TableCell>
                        <TableCell>{formatDate(transfer.received_at)}</TableCell>
                        <TableCell>{getStatusChip(transfer.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </TabPanel>
      </Card>

      {/* Receive Dialog */}
      <Dialog
        open={receiveDialogOpen}
        onClose={() => setReceiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <CallReceived sx={{ mr: 1, verticalAlign: 'middle' }} />
          Recevoir le Transfert
        </DialogTitle>
        <DialogContent>
          {selectedTransfer && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    N° Transfert
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedTransfer.transfer_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Lot
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransfer.batch_number || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Origine
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransfer.from_location_code}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Nombre d'échantillons
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {selectedTransfer.sample_count}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes de réception (optionnel)"
                    multiline
                    rows={3}
                    value={receiveNotes}
                    onChange={(e) => setReceiveNotes(e.target.value)}
                    placeholder="Observations sur l'état des échantillons..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReceiveDialogOpen(false)}
            disabled={receiving}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleReceiveTransfer}
            disabled={receiving}
            startIcon={receiving ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            Confirmer la Réception
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransferDashboard;
